/**
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import React, {
  ChangeEvent, FormEvent, useEffect, useMemo, useState
} from 'react';
import {
  IlButton,
  IlInlineLoading,
  IlTextInput,
  IlInlineNotification,
  IlSelect,
  IlSelectItem
} from 'il-framework-component';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import {
  DEFAULT_SMS_DIGITS,
  FORBIDDEN_CHARACTERS_REGEX,
  I18nNamespaces,
  LABEL_MAX_LENGTH,
  LOA_RESEND_SECONDS,
  ValidationErrorKey as ValErr
} from '../../../../../app/shared/utils/constants.util';
import { RootState } from '../../../../../app/store';
import { AuthErrorKey } from '../../../../../domain/models/impl/SecuritySetup';
import { getLoaValidationMessageKey } from '../../../../../app/shared/utils/helpers.util';
import {
  setError,
  setValidationError,
  profileActivateFactor,
  profileChallengeFactor,
  profileEnrollSMSFactor,
  resetProfileEnrollState
} from '../../profileEnrollSlice';
import countries from '../../../../../app/shared/utils/country-list.json';
import styles from './ProfileEnrollSMS.scss';

type Country = { name: string; dial_code: string; code: string };

const getDialCode = (iso: string, list: Country[]): string => {
  if (iso === 'Other') return '';
  return list.find((c) => c.code === iso)?.dial_code ?? '';
};

interface ProfileEnrollSMSProps {
  loaLevel: number;
  onEnrolled: () => void;
  onCancel: () => void;
}

const ProfileEnrollSMS: React.FC<ProfileEnrollSMSProps> = ({
  loaLevel,
  onEnrolled,
  onCancel
}) => {
  const { t } = useTranslation([I18nNamespaces.LOA_ENRICHMENT, I18nNamespaces.COUNTRIES_NAMES]);
  const dispatch = useDispatch();
  const actionLoading = useSelector((s: RootState) => s.profileEnroll.actionLoading);
  const validationError = useSelector((s: RootState) => s.profileEnroll.validationError);
  const smsErrorKey = useSelector((s: RootState) => s.profileEnroll.error);
  const selectedFactor = useSelector((s: RootState) => s.profileEnroll.selectedFactor);

  const [label, setLabel] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState<number>(0);
  const [step, setStep] = useState<'details' | 'code'>('details');

  const factorId = selectedFactor?.factorId ?? '';
  const digits = selectedFactor?.digits ?? DEFAULT_SMS_DIGITS;
  const phone = (selectedFactor?.data ?? '') as string;

  const typedDigits = code.replace(/\D/g, '').length;
  const shouldShowInlineNotification = !!smsErrorKey;

  const isLabelError = validationError?.startsWith('label');
  const isPhoneNumberError = !!validationError && !isLabelError;

  const parsedPhone = useMemo(() => {
    const raw = phoneNumber.trim();
    if (!raw) return null;

    try {
      if (countryCode === 'Other') {
        if (!raw.startsWith('+')) return null;
        return parsePhoneNumberWithError(raw);
      }

      return parsePhoneNumberWithError(raw, countryCode as CountryCode);
    } catch {
      return null;
    }
  }, [phoneNumber, countryCode]);

  const dialCode = useMemo(() => {
    if (countryCode === 'Other') {
      const cc = parsedPhone?.countryCallingCode;
      return cc ? `+${cc}` : '';
    }

    return getDialCode(countryCode, countries as Country[]);
  }, [countryCode, parsedPhone]);

  const isPhoneValid = Boolean(parsedPhone?.isValid());
  const nationalNumber = parsedPhone ? String(parsedPhone.nationalNumber) : '';

  const canNext = Boolean(label.trim())
    && Boolean(dialCode)
    && isPhoneValid
    && !actionLoading;

  const canResend = Boolean(factorId) && resendIn === 0 && !actionLoading;

  const canSubmit = Boolean(factorId)
    && digits > 0
    && !actionLoading
    && !validationError
    && typedDigits === digits;

  const inlineTitleMapAddPhone: Record<string, string> = {
    [AuthErrorKey.ResourceAlreadyExists]: t('loaEnrichment.factors.sms.inline.addPhoneAlreadyExists'),
    [AuthErrorKey.ResourceForbidden]: t('loaEnrichment.factors.sms.inline.addPhoneForbidden'),
    [AuthErrorKey.DefaultErrorMessage]: t('loaEnrichment.factors.sms.inline.addPhoneFailed')
  };

  const inlineTitleAddPhone = inlineTitleMapAddPhone[smsErrorKey]
    ?? t('loaEnrichment.factors.sms.inline.addPhoneFailed');

  const inlineTitleMapActivateSMS: Record<string, string> = {
    [AuthErrorKey.DefaultErrorMessage]: t('loaEnrichment.factors.sms.inline.activateFailed'),
    [AuthErrorKey.DefaultSMSChallengeError]: t('loaEnrichment.common.errors.challengeFailedInline')
  };

  const inlineTitleActivateSMS = inlineTitleMapActivateSMS[smsErrorKey]
    ?? t('loaEnrichment.common.errors.challengeFailedInline');

  const clearErrors = () => {
    dispatch(setValidationError(''));
    dispatch(setError(''));
  };

  const updateCountryCode = (e: React.ChangeEvent<HTMLInputElement>): void => {
    clearErrors();
    setCountryCode(e.currentTarget.value);
  };

  const updatePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>): void => {
    clearErrors();
    setPhoneNumber(e.currentTarget.value.replace(/[^0-9+ \-.()]|[.\- ()]{2}/g, ''));
  };

  const updateLabel = (e: React.ChangeEvent<HTMLInputElement>): void => {
    clearErrors();
    const normalize = e.target.value;
    if (normalize.length > LABEL_MAX_LENGTH) {
      dispatch(setValidationError(ValErr.LabelTooLong));
    } else if (validationError?.startsWith('label')) {
      dispatch(setValidationError(''));
    }
    setLabel(e.currentTarget.value);
  };

  const sendCode = async () => {
    if (!factorId) return;
    setResendIn(LOA_RESEND_SECONDS);
    await dispatch(profileChallengeFactor(
      t,
      {
        factorId,
        errorKey: AuthErrorKey.DefaultSMSChallengeError,
        toastKey: 'loaEnrichment.common.errors.sendFailedToast'
      }
    ) as any);
  };

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => {
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => () => {
    dispatch(resetProfileEnrollState());
  }, [dispatch]);

  const challengedOnceRef = React.useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (step !== 'code') return;
    if (!factorId) return;
    if (challengedOnceRef.current[factorId]) return;

    challengedOnceRef.current[factorId] = true;
    sendCode();
  }, [step, factorId]);

  const onNext = async (ev: FormEvent | ChangeEvent) => {
    ev.preventDefault();
    clearErrors();

    const normalizedLabel = label.trim();

    if (!normalizedLabel) {
      dispatch(setValidationError(ValErr.LabelRequired));
      return;
    }

    if (normalizedLabel.length > LABEL_MAX_LENGTH) {
      dispatch(setValidationError(ValErr.LabelTooLong));
      return;
    }

    if (FORBIDDEN_CHARACTERS_REGEX.test(normalizedLabel)) {
      dispatch(setValidationError(ValErr.LabelForbiddenChars));
      return;
    }

    if (!parsedPhone || !parsedPhone.isValid()) {
      dispatch(setValidationError(ValErr.SmsInvalidPhone));
      return;
    }

    const newFactorId = await dispatch(profileEnrollSMSFactor(t, {
      label: normalizedLabel,
      countryCode: dialCode,
      nationalNumber,
      loaLevel
    }) as any) as string | undefined;

    if (!newFactorId) return;

    setCode('');
    setResendIn(0);
    setStep('code');
  };

  const updateCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    const input = e.currentTarget.value.replace(/\D/g, '');
    setCode(input.slice(0, digits));
  };

  const submit = async (ev: FormEvent | ChangeEvent) => {
    ev.preventDefault();
    if (!factorId) return;

    const success = await dispatch(profileActivateFactor(t, {
      factorId,
      code: code.replace(/\s/g, ''),
      action: 'activate'
    }) as any);

    if (success) {
      onEnrolled();
    }
  };

  return (
    <>
      <h1 className={styles.formTitle}>
        {t('loaEnrichment.factors.sms.title')}
      </h1>

      {step === 'details' && (
        <>
          <p className={styles.helpText}>
            {t('loaEnrichment.factors.sms.enrollSubtitle')}
          </p>

          {shouldShowInlineNotification && (
            <div className={styles.inlineNotification}>
              <IlInlineNotification
                title={inlineTitleAddPhone}
                notificationType='inline'
                kind='error'
                hideCloseButton
              />
            </div>
          )}

          <form onSubmit={onNext}>
            <div className={styles.detailsContainer}>
              <IlTextInput
                id='profile-sms-label'
                labelText={t('loaEnrichment.factors.sms.fields.label')}
                placeholder={t('loaEnrichment.common.placeholders.label')}
                type='text'
                onChange={updateLabel}
                invalid={isLabelError}
                invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
                value={label}
                autoFocus
              />

              <IlSelect
                labelText={t('loaEnrichment.factors.sms.fields.countryCode')}
                id='profile-country-code'
                onChange={updateCountryCode}
                value={countryCode}
              >
                {(countries as Country[]).map((country) => (
                  <IlSelectItem
                    key={country.code}
                    value={country.code}
                    text={`${t(country.name, { ns: I18nNamespaces.COUNTRIES_NAMES })} (${country.dial_code})`}
                  />
                ))}
                <IlSelectItem
                  key='Other'
                  value='Other'
                  text={`${t('OTHER', { ns: I18nNamespaces.COUNTRIES_NAMES })}`}
                />
              </IlSelect>

              <IlTextInput
                id='profile-phone-number'
                labelText={t('loaEnrichment.factors.sms.fields.phoneNumber')}
                placeholder={countryCode === 'Other' ? '+1 415 555 2671' : '000-000-0000'}
                type='text'
                onChange={updatePhoneNumber}
                invalid={isPhoneNumberError}
                invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
                value={phoneNumber}
              />
            </div>

            <div className={styles.nextBtnContainer}>
              <IlButton kind='secondary' type='button' onClick={onCancel}>
                {t('loaEnrichment.common.buttons.cancel')}
              </IlButton>
              <IlButton kind='primary' type='submit' disabled={!canNext}>
                {t('loaEnrichment.common.buttons.next')}
              </IlButton>
            </div>
          </form>

          {actionLoading && (
            <div className={styles.loadingContainer}>
              <IlInlineLoading
                description={t('loaEnrichment.common.status.pleaseWait')}
                success={false}
                onSuccess={noop}
              />
            </div>
          )}
        </>
      )}

      {step === 'code' && (
        <>
          <p className={styles.helpText}>
            {t('loaEnrichment.factors.sms.verifyHelpText', {
              phoneNumber: phone || t('loaEnrichment.factors.sms.fallbackPhoneNumber')
            })}
          </p>

          {shouldShowInlineNotification && (
            <div className={styles.inlineNotification}>
              <IlInlineNotification
                title={inlineTitleActivateSMS}
                notificationType='inline'
                kind='error'
                hideCloseButton
              />
            </div>
          )}

          <form onSubmit={submit}>
            <IlTextInput
              id='profile-sms-activation-code'
              labelText={t('loaEnrichment.common.labels.verificationCode')}
              placeholder={t('loaEnrichment.common.placeholders.code', { digits })}
              type='text'
              maxLength={digits}
              invalid={!!validationError}
              invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
              value={code}
              onChange={updateCode}
              autoFocus
              autoComplete='off'
            />

            <div className={styles.activationContainer}>
              <div className={styles.resendContainer}>
                <p className={styles.resendText}>{t('loaEnrichment.common.resend.smsPrompt')}</p>
                <IlButton
                  className={styles.resendBtn}
                  kind='ghost'
                  type='button'
                  disabled={!canResend}
                  onClick={sendCode}
                >
                  {resendIn > 0
                    ? t('loaEnrichment.common.resend.resendIn', { seconds: resendIn })
                    : t('loaEnrichment.common.buttons.resendCode')}
                </IlButton>
              </div>

              <div className={styles.actionBtnsContainer}>
                <IlButton
                  kind='secondary'
                  type='button'
                  onClick={() => {
                    clearErrors();
                    setCode('');
                    setResendIn(0);
                    setStep('details');
                  }}
                >
                  {t('loaEnrichment.common.buttons.back')}
                </IlButton>
                {' '}
                <IlButton kind='primary' type='submit' disabled={!canSubmit}>
                  {t('loaEnrichment.common.buttons.activate')}
                </IlButton>
              </div>
            </div>
          </form>

          {actionLoading && (
            <div className={styles.loadingContainer}>
              <IlInlineLoading
                description={t('loaEnrichment.common.status.pleaseWait')}
                success={false}
                onSuccess={noop}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ProfileEnrollSMS;
